import AddIcon from "@mui/icons-material/Add";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import { Button } from "@mui/material";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import { useTheme } from "@mui/material/styles";
import type React from "react";

import useDevMode from "../../utils/devmode";
import Modal from "../Modal";

export interface AddEntityActionsProps {
  addEntityForm: React.ReactNode;
  addAIForm?: React.ReactNode;
  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  handleAISubmit?: (e: React.FormEvent<HTMLFormElement>) => void;
  isDisabled: boolean;
  isAIDisabled?: boolean;
  enableAddToolBar: boolean;
  enableAddRow?: boolean;
  enableAddWithAI?: boolean;
  entityName: string;
  setOpenModal: (open: boolean) => void;
  openModal: boolean;
  setOpenAIModal?: (open: boolean) => void;
  openAIModal?: boolean;
}

export default function AddEntityActions({
  addEntityForm,
  addAIForm,
  handleSubmit,
  handleAISubmit,
  isDisabled,
  isAIDisabled,
  enableAddToolBar,
  enableAddRow = true,
  enableAddWithAI,
  entityName,
  setOpenModal,
  openModal,
  setOpenAIModal,
  openAIModal = false,
}: AddEntityActionsProps) {
  const theme = useTheme();
  const devMode = useDevMode();

  return (
    <>
      {enableAddToolBar && devMode && (
        <>
          <Modal
            title={`Add/Edit ${entityName}`}
            open={openModal}
            onClose={() => {
              setOpenModal(false);
            }}
          >
            <Grid
              container
              justifyContent="center"
              sx={{ height: "100%", margin: `${theme.spacing(7)} 0` }}
            >
              <Grid item xs={12} sm={5}>
                <Box
                  component="form"
                  onSubmit={(data) => {
                    handleSubmit(data);
                    setOpenModal(false);
                  }}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {addEntityForm}
                  <Button
                    fullWidth
                    sx={{ borderRadius: "8px", marginBottom: "2rem" }}
                    type="submit"
                    autoFocus
                    color="success"
                    variant="contained"
                    disabled={isDisabled}
                    data-testid="saveEntity"
                  >
                    {`Save ${entityName}`}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Modal>
          {enableAddRow && (
            <Button
              color="primary"
              startIcon={<AddIcon />}
              data-testid={`addRow${entityName}`}
              onClick={() => {
                setOpenModal(true);
              }}
            >
              Add Row
            </Button>
          )}
        </>
      )}
      {enableAddWithAI && devMode && handleAISubmit && (
        <>
          <Modal
            title={`Upload ${entityName} for AI`}
            open={openAIModal}
            onClose={() => {
              if (setOpenAIModal) {
                setOpenAIModal(false);
              }
            }}
          >
            <Grid
              container
              justifyContent="center"
              sx={{ height: "100%", margin: `${theme.spacing(7)} 0` }}
            >
              <Grid item xs={12} sm={5}>
                <Box
                  component="form"
                  onSubmit={(data) => {
                    handleAISubmit(data);
                    if (setOpenAIModal) {
                      setOpenAIModal(false);
                    }
                  }}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {addAIForm}
                  <Button
                    fullWidth
                    sx={{ borderRadius: "8px", marginBottom: "2rem" }}
                    type="submit"
                    autoFocus
                    color="success"
                    variant="contained"
                    disabled={isAIDisabled}
                    data-testid="saveAIEntity"
                  >
                    {`Save ${entityName}`}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Modal>
          <Button
            color="primary"
            startIcon={<SmartToyIcon />}
            data-testid={`addRowAI${entityName}`}
            onClick={() => {
              if (setOpenAIModal) {
                setOpenAIModal(true);
              }
            }}
          >
            Add With AI
          </Button>
        </>
      )}
    </>
  );
}
